/**
 * Wire contract for the `/datte/mf` endpoints — the client-side SSoT (spec §3.2/§3.5).
 *
 * The Block-2 PHP wire VOs (`SlimNode`/`SubtreeResponse`/`LevelRow`) are
 * mirrored here as zod schemas; `zod.parse` is the runtime trust boundary at
 * `callEndpoint` (data/endpoint.ts). `TransferNode` IS the internal `Node` (no
 * mapper — wire == internal, Decision D7): the tree-service stores parsed nodes
 * directly.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.2
 */

import { z } from 'zod';

import { PUBLIC_ID_PATTERN, nodeId } from '../domain/brand';

/** Level 1..7 — a strict literal union (mirrors PHP SlimNode.level 1..7). */
export const LevelSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);
export type Level = z.infer<typeof LevelSchema>;

/**
 * A node id on the wire: a number (legacy `alt` source incl. negative virtual
 * series ids, and the virtual root `0`) OR a 22-char base64url public id (`datte`
 * source, W0-4 spec §3.4). Branded via `transform(nodeId)` (ADR-0011 §4).
 */
const WireNodeId = z.union([z.number(), z.string().regex(PUBLIC_ID_PATTERN)]).transform(nodeId);

/**
 * A slim catalog node — the wire projection of the PHP `ModelFinderNode`.
 * `hasChildren` is the explicit leaf marker: `false` ⇒ terminal "model"; the
 * client renders no further column and uses `url` as the parts-catalog CTA
 * (§3.5). It is independent of `url`.
 */
export const TransferNode = z.object({
  // Brand ids at the wire (ADR-0011 §4): `transform` runs at parse time and
  // returns its input unchanged, so the emitted JS is identical but
  // `z.infer<typeof TransferNode>['id']` IS the hand-rolled `NodeId` — the same
  // brand the DOM boundaries produce via `nodeId(...)`. NOT zod `.brand()`
  // (its symbol shape is incompatible with the hand-rolled brand).
  id: WireNodeId,
  parentId: WireNodeId.nullable(),
  name: z.string(),
  url: z.string().nullable(),
  imageUrl: z.string().nullable(),
  level: LevelSchema,
  hasChildren: z.boolean(),
});
/** Wire node == internal node (no mapper) — D7. */
export type Node = z.infer<typeof TransferNode>;

/** Per-level title overrides (the adaptive "Serie"↔"Serie 1" rule arrives here). */
export const LevelLabelOverride = z.record(z.string(), z.object({ title: z.string() }));
export type LevelLabelOverride = z.infer<typeof LevelLabelOverride>;

/**
 * The subtree response. NO version field — the URL is the version SSoT; the
 * fresh hash after a 302 is read from `Response.url` (data/endpoint.ts), not
 * the body. `nodes` is keyed by stringified id (JSON object) — a public id or a
 * stringified number. The KEY is never read back as an id: the tree-service
 * stores `Object.values(nodes)` by each node's own parsed `id`, so a numeric key
 * `"8181"` and a public-id key both work without a `Number(key)`.
 */
export const SubtreeResponse = z.object({
  requestedId: WireNodeId,
  depth: z.number(),
  // The column plan: one level number PER COLUMN POSITION, in cascade order
  // (spine ++ below-cascade ++ forced-model; spec §3.1/§3.2, ADR-0009). The list
  // is non-decreasing but MAY REPEAT a level — a `series`→`intermediate` spine
  // projects two adjacent level-5 columns (e.g. `[1,2,5,5,7]`), so
  // `columns.length` is the real column count. REQUIRED. The client treats the
  // order as authoritative, renders exactly `columns.length` columns, and does
  // NOT re-sort or dedup; each position's level is used only to label that column.
  // zod validates element type only; the per-position / non-decreasing guarantees
  // are PHP-side invariants (ColumnPlannerTest).
  columns: z.array(LevelSchema),
  levelLabels: LevelLabelOverride.optional(),
  ancestors: z.array(TransferNode), // root → parent of requestedId ([] if root/unknown)
  nodes: z.record(z.string(), TransferNode),
});
export type SubtreeResponse = z.infer<typeof SubtreeResponse>;

/**
 * `callEndpoint` re-attaches the FINAL (post-redirect) response URL so the
 * tree-service can adopt a fresh version hash after a 302 (§3.3). zod strips
 * unknown keys, so `finalUrl` is added AFTER `.parse()`, not in the schema.
 */
export type SubtreeResult = SubtreeResponse & { finalUrl: string };

/** `/levels` — symmetric SSoT; `key` is a strict enum (mirrors PHP LevelRow). */
export const LevelKeySchema = z.enum([
  'manufacturer',
  'deviceType',
  'deviceSubtype',
  'driveType',
  'series1',
  'series2',
  'model',
]);
export const LevelRow = z.object({
  level: LevelSchema,
  key: LevelKeySchema,
  title: z.string(),
  selectLabel: z.string(),
  clearLabel: z.string(),
});
export const LevelsResponse = z.array(LevelRow);
export type LevelRow = z.infer<typeof LevelRow>;
export type LevelsResponse = z.infer<typeof LevelsResponse>;
